import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ProfileLayout } from "@/components/ProfileLayout";
import { ContentSection } from "@/components/ContentSection";
import { useCourseIndex } from "@/lib/course";
import { CourseCardForSlug, type CourseStatus } from "@/components/CourseCard";

export default function MyTraining() {
  return (
    <ProfileLayout title="My Training">
      <ContentSection id="my-training-intro" />

      <h2 className="text-lg font-semibold mb-3">Your courses</h2>
      <MyCourses />
    </ProfileLayout>
  );
}

// Shows only the courses the student is actually engaged with — in-progress or
// completed. Not-started courses live in the catalog (/courses), not here.
function MyCourses(): JSX.Element {
  const index = useCourseIndex();
  const [statuses, setStatuses] = useState<Record<string, CourseStatus>>({});

  const onStatusResolved = useCallback((slug: string, status: CourseStatus) => {
    setStatuses((prev) => (prev[slug] === status ? prev : { ...prev, [slug]: status }));
  }, []);

  if (index.status === "loading") {
    return <p className="text-muted-foreground">Loading courses…</p>;
  }
  if (index.status === "error") {
    return <p className="text-destructive">Failed to load courses: {index.message}</p>;
  }

  const slugs = index.courses.map((c) => c.slug);
  const allResolved = slugs.length > 0 && slugs.every((s) => statuses[s] !== undefined);
  const startedCount = slugs.filter(
    (s) => statuses[s] === "in-progress" || statuses[s] === "completed"
  ).length;
  const showEmpty = slugs.length === 0 || (allResolved && startedCount === 0);

  return (
    <>
      <div className="space-y-4">
        {slugs.map((slug) => (
          <CourseCardForSlug
            key={slug}
            courseSlug={slug}
            showWhen={(s) => s !== "not-started"}
            onStatusResolved={onStatusResolved}
          />
        ))}
      </div>
      {showEmpty && (
        <p className="text-muted-foreground">
          You haven&rsquo;t started any courses yet.{" "}
          <Link to="/courses" className="text-primary-soft hover:underline">
            Browse the catalog →
          </Link>
        </p>
      )}
    </>
  );
}
